import { pluralise } from '@/utilities/pluralise';
import { Heading, Text, Grid, useTheme } from '@simplywallst/ui-core';
import type { HeadingElements } from '@simplywallst/ui-core/lib/src/components/Heading/types';

import BriefIcon, { getFill } from './BriefIcon';

import type { Theme } from '@simplywallst/ui-core';

import {
  Item,
  ArticleIcon,
  IconContainer,
  SeekingAlphaIcon,
  AnnouncementIcon,
  StyledMoreUpdates,
} from './styled';
import type { FeedItem } from './types';

interface Props {
  feedItem: FeedItem;
  headingElement?: HeadingElements;
  hoverBackground?: keyof Theme['color'];
}

export const FeedCardItem = ({
  feedItem,
  hoverBackground = 'phantom',
  headingElement = 'h3',
}: Props) => {
  const theme = useTheme();

  const { id, dateFormatted, type, thumbnail, outcome, title } = feedItem;
  const briefFill = getFill(id, outcome);

  function getMoreUpdatesSubtitle() {
    const uniqueUpdatesIds = Array.from(new Set(id.split(',')));
    const moreUpdatesCount = uniqueUpdatesIds.length - 1;

    if (moreUpdatesCount > 0) {
      return (
        <StyledMoreUpdates>
          <Text as="span" typography="tiny" color="whiteRGBA" opacity={0.5}>
            + {moreUpdatesCount} more{' '}
            {pluralise(moreUpdatesCount, 'update', 'updates')}
          </Text>
        </StyledMoreUpdates>
      );
    }
  }

  return (
    <Item
      hoverBackground={hoverBackground}
      as="article"
      gap={`${theme.x3spacePx / 2}px`}
      templateColumns="min-content auto"
      alignItems="start"
      data-cy-id="feed-card-item"
    >
      {type === 'event' ? (
        <>
          <IconContainer alignItems="center" justifyContent="center">
            <AnnouncementIcon />
          </IconContainer>
          <Grid rowGap={`${theme.x1spacePx / 2}px`}>
            {title ? (
              <Heading
                as={headingElement}
                typography="xSmall"
                color="white"
                lineClamp={3}
              >
                {title}
              </Heading>
            ) : null}
            <Text as="span" typography="tiny" color="whiteRGBA" opacity={0.5}>
              {dateFormatted}
            </Text>
          </Grid>
        </>
      ) : type === 'multi-event' ? (
        <>
          <IconContainer alignItems="center" justifyContent="center">
            <AnnouncementIcon />
          </IconContainer>
          <Grid rowGap={`${theme.x1spacePx}px`}>
            {title ? (
              <Heading
                as={headingElement}
                typography="xSmall"
                color="white"
                lineClamp={3}
              >
                {title}
              </Heading>
            ) : null}

            <Grid
              templateColumns="max-content max-content"
              gap={`${theme.x1spacePx}px`}
              alignItems="center"
            >
              <Text as="span" typography="tiny" color="whiteRGBA" opacity={0.5}>
                {dateFormatted}
              </Text>
              {getMoreUpdatesSubtitle()}
            </Grid>
          </Grid>
        </>
      ) : type === 'article' ? (
        <>
          <IconContainer alignItems="center" justifyContent="center">
            <ArticleIcon />
          </IconContainer>
          <Grid templateColumns="80% auto" gap={`${theme.x1spacePx}px`}>
            {title ? (
              <Grid rowGap={`${theme.x1spacePx}px`}>
                <Heading
                  as={headingElement}
                  typography="xSmall"
                  color="white"
                  lineClamp={3}
                >
                  {title}
                </Heading>
                <Text
                  as="span"
                  typography="tiny"
                  color="whiteRGBA"
                  opacity={0.5}
                >
                  {dateFormatted}
                </Text>
              </Grid>
            ) : null}
            <Grid>
              <img
                src={thumbnail}
                alt={title}
                loading="lazy"
                width={56}
                height={40}
              />
            </Grid>
          </Grid>
        </>
      ) : type === 'external-article' ? (
        <>
          <IconContainer alignItems="center" justifyContent="center">
            <SeekingAlphaIcon />
          </IconContainer>
          <Grid rowGap={`${theme.x1spacePx / 2}px`}>
            {title ? (
              <Heading
                as={headingElement}
                typography="xSmall"
                color="white"
                lineClamp={3}
              >
                {title}
              </Heading>
            ) : null}{' '}
            <Text as="span" typography="tiny" color="whiteRGBA" opacity={0.5}>
              {dateFormatted}
            </Text>
          </Grid>
        </>
      ) : type === 'brief' ? (
        <>
          <IconContainer
            alignItems="center"
            justifyContent="center"
            fill={briefFill}
          >
            <BriefIcon id={id} outcome={outcome} />
          </IconContainer>
          <Grid rowGap={`${theme.x1spacePx / 2}px`}>
            {title ? (
              <Heading
                as={headingElement}
                typography="xSmall"
                color="white"
                lineClamp={3}
              >
                {title}
              </Heading>
            ) : null}{' '}
            <Text as="span" typography="tiny" color="whiteRGBA" opacity={0.5}>
              {dateFormatted}
            </Text>
          </Grid>
        </>
      ) : null}
    </Item>
  );
};
